import React, { useState } from 'react';
import { Star, Quote, Play, ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { testimonials } from '../utils/mock';

const TestimonialsV2 = () => {
  const [selectedTestimonial, setSelectedTestimonial] = useState(0);

  const videoTestimonials = [
    {
      id: 1,
      name: "Rahul Verma",
      country: "Canada",
      thumbnail: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d",
      duration: "2:45"
    },
    {
      id: 2,
      name: "Sneha Reddy",
      country: "Australia", 
      thumbnail: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80",
      duration: "3:12"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-20 w-80 h-80 bg-[#FF7043] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#1565C0] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-[#FF7043] to-[#F4511E] text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
              Student Success Stories
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Dreams Realized, Lives Transformed
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Hear directly from students who chose Visaspot and are now thriving in their dream countries.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
          {/* Featured Testimonial */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-2xl bg-gradient-to-br from-white to-blue-50 overflow-hidden h-full">
              <CardContent className="p-0">
                <div className="grid grid-cols-1 md:grid-cols-5 h-full">
                  {/* Image Side */}
                  <div className="md:col-span-2 relative">
                    <img 
                      src={testimonials[selectedTestimonial].image}
                      alt={testimonials[selectedTestimonial].name}
                      className="w-full h-full object-cover min-h-[300px]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="text-white font-bold text-2xl mb-1">
                        {testimonials[selectedTestimonial].name}
                      </div>
                      <div className="text-white/90 text-sm mb-2">
                        {testimonials[selectedTestimonial].university}
                      </div>
                      <div className="inline-block bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm">
                        📍 {testimonials[selectedTestimonial].country}
                      </div>
                    </div>
                  </div>

                  {/* Content Side */}
                  <div className="md:col-span-3 p-8 md:p-12 flex flex-col justify-between">
                    <div>
                      {/* Quote Icon */}
                      <Quote size={48} className="text-[#1565C0]/20 mb-6" />

                      {/* Rating */}
                      <div className="flex gap-1 mb-6">
                        {[...Array(testimonials[selectedTestimonial].rating)].map((_, i) => (
                          <Star key={i} size={24} className="fill-[#FF7043] text-[#FF7043]" />
                        ))}
                      </div>

                      {/* Testimonial Text */}
                      <p className="text-xl text-gray-700 leading-relaxed mb-8 italic">
                        "{testimonials[selectedTestimonial].text}"
                      </p>
                    </div>

                    {/* Navigation Dots */}
                    <div className="flex gap-3">
                      {testimonials.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedTestimonial(index)}
                          className={`transition-all duration-300 rounded-full ${
                            selectedTestimonial === index 
                              ? 'w-12 h-3 bg-[#1565C0]' 
                              : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Video Testimonials */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-[#1565C0] to-[#0D47A1] rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Play size={24} />
                <h3 className="text-xl font-bold">Video Testimonials</h3>
              </div>
              <p className="text-white/90 text-sm mb-6">
                Watch real students share their journey with Visaspot
              </p>

              <div className="space-y-4">
                {videoTestimonials.map((video) => (
                  <div 
                    key={video.id}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="relative flex-shrink-0">
                        <img 
                          src={video.thumbnail}
                          alt={video.name}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg group-hover:bg-black/50 transition-colors">
                          <Play size={20} className="text-white fill-white" />
                        </div>
                        <div className="absolute bottom-1 right-1 bg-black/70 px-1.5 py-0.5 rounded text-xs text-white">
                          {video.duration}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-white truncate">
                          {video.name}
                        </div>
                        <div className="text-sm text-white/80">
                          Now in {video.country}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Card */}
            <Card className="border-0 shadow-xl bg-gradient-to-br from-orange-50 to-white">
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-gray-900 mb-2">4.9/5</div>
                  <div className="flex justify-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={20} className="fill-[#FF7043] text-[#FF7043]" />
                    ))}
                  </div>
                  <div className="text-gray-600 mb-4">Based on 5,000+ reviews</div>
                  <a 
                    href="#" 
                    className="inline-flex items-center gap-2 text-[#1565C0] hover:text-[#FF7043] font-semibold text-sm transition-colors"
                  >
                    Read all reviews
                    <ExternalLink size={16} />
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Success Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {[
            { number: "10,000+", label: "Students Placed", icon: "🎓" },
            { number: "95%", label: "Visa Success", icon: "✅" },
            { number: "25+", label: "Countries", icon: "🌍" },
            { number: "100%", label: "Satisfaction", icon: "⭐" }
          ].map((metric, index) => (
            <div 
              key={index}
              className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="text-4xl mb-3">{metric.icon}</div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{metric.number}</div>
              <div className="text-sm text-gray-600">{metric.label}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-xl text-gray-600 mb-6">
            Want to be our next success story?
          </p>
          <button 
            onClick={() => {
              const element = document.getElementById('consultation');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-gradient-to-r from-[#1565C0] to-[#0D47A1] text-white px-10 py-5 rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 inline-flex items-center gap-3"
          >
            Start Your Success Story
            <Star size={20} className="fill-white" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsV2;
